import { BlobServiceClient } from "@azure/storage-blob";
import { STORAGE_CONFIG } from "../authConfig";

const { accountName, containerName, blobName, sasToken } = STORAGE_CONFIG;
const USERS_BLOB = "users.json";
const ANNOUNCEMENTS_BLOB = "announcements.json";
const SETTINGS_BLOB = "settings.json";
const ACCESS_BLOB = "access.json";
const AUDIT_BLOB = "audit.json";
const REACTIONS_BLOB = "reactions.json";
const READS_BLOB = "reads.json";
const AUDIT_RETENTION_DAYS = 15;
const blobUrl = `https://${accountName}.blob.core.windows.net?${sasToken}`;

// Categorias (sub-abas) padrão — usadas para recados e cards de links.
// Servem de fallback quando settings.json ainda não tem uma lista própria.
export const DEFAULT_CATEGORIES = ["P&C", "SQL Server", "BI", "Postgres", "Oracle", "Marketing", "Adm/Fin", "Comercial"];

// Resolve a lista de categorias a partir de settings, caindo no padrão
// quando o campo não existe ou está vazio.
export function resolveCategories(settings) {
  const list = settings && settings.categories;
  return Array.isArray(list) && list.length > 0 ? list : DEFAULT_CATEGORIES;
}

function getContainerClient() {
  const client = new BlobServiceClient(blobUrl);
  return client.getContainerClient(containerName);
}

async function readJsonBlob(name) {
  const container = getContainerClient();
  const blobClient = container.getBlobClient(name);
  const download = await blobClient.download();
  const blob = await download.blobBody;
  const text = await blob.text();
  return JSON.parse(text);
}

async function writeJsonBlob(name, data) {
  const container = getContainerClient();
  const blockBlob = container.getBlockBlobClient(name);
  const content = JSON.stringify(data, null, 2);
  await blockBlob.upload(content, content.length, {
    blobHTTPHeaders: { blobContentType: "application/json" },
  });
}

export async function loadLinks() {
  try {
    return await readJsonBlob(blobName);
  } catch (err) {
    console.warn("loadLinks falhou, usando defaults:", err);
    return getDefaultLinks();
  }
}

export async function saveLinks(data) {
  await writeJsonBlob(blobName, data);
}

export async function loadUsers() {
  try {
    return await readJsonBlob(USERS_BLOB);
  } catch (err) {
    console.warn("loadUsers falhou, usando defaults:", err);
    return getDefaultUsers();
  }
}

export async function saveUsers(data) {
  await writeJsonBlob(USERS_BLOB, data);
}

export async function loadAnnouncements() {
  try {
    return await readJsonBlob(ANNOUNCEMENTS_BLOB);
  } catch (err) {
    console.warn("loadAnnouncements falhou, usando defaults:", err);
    return { announcements: [] };
  }
}

export async function saveAnnouncements(data) {
  await writeJsonBlob(ANNOUNCEMENTS_BLOB, data);
}

// Limites do uploadImage — defesa em profundidade (o SAS ainda permite qualquer write,
// mas o fluxo da UI rejeita antes de tocar no blob).
const UPLOAD_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const UPLOAD_ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const UPLOAD_ALLOWED_EXTS = ["png", "jpg", "jpeg", "webp", "gif"];

export async function uploadImage(file, folder = "announcements") {
  if (!file) throw new Error("Nenhum arquivo informado");
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("O arquivo precisa ser uma imagem");
  }
  // SVG não é aceito: permite <script> embutido, vira vetor de XSS se aberto direto.
  if (!UPLOAD_ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`Formato não suportado (${file.type}). Use PNG, JPG, WEBP ou GIF.`);
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    throw new Error(`Imagem muito grande (${mb} MB). O limite é 5 MB.`);
  }
  const container = getContainerClient();
  let ext = (file.name && file.name.includes(".")) ? file.name.split(".").pop().toLowerCase() : (file.type.split("/")[1] || "png");
  // Normaliza extensões conhecidas e rejeita qualquer coisa fora da whitelist
  if (ext === "jpe") ext = "jpg";
  if (!UPLOAD_ALLOWED_EXTS.includes(ext)) ext = "png";
  const rand = Math.random().toString(36).slice(2, 8);
  const name = `${folder}/${Date.now()}-${rand}.${ext}`;
  const blockBlob = container.getBlockBlobClient(name);
  const arrayBuffer = await file.arrayBuffer();
  await blockBlob.uploadData(arrayBuffer, {
    blobHTTPHeaders: { blobContentType: file.type || "image/png" },
  });
  // Retorna a URL pública (com SAS) pronta para uso em <img src>
  return `https://${accountName}.blob.core.windows.net/${containerName}/${name}?${sasToken}`;
}

export async function loadSettings() {
  try {
    return await readJsonBlob(SETTINGS_BLOB);
  } catch (err) {
    console.warn("loadSettings falhou, usando defaults:", err);
    return { logoUrl: "" };
  }
}

export async function saveSettings(data) {
  await writeJsonBlob(SETTINGS_BLOB, data);
}

// ── Log de último acesso por usuário ──────────────────────────
export async function loadAccessLog() {
  try {
    return await readJsonBlob(ACCESS_BLOB);
  } catch {
    return { accesses: {} };
  }
}

export async function logAccess({ email, name }) {
  if (!email) return;
  try {
    const current = await loadAccessLog();
    const accesses = current.accesses || {};
    const key = email.toLowerCase();
    const previous = accesses[key] || {};
    accesses[key] = {
      email: key,
      name: name || previous.name || "",
      lastAccess: new Date().toISOString(),
      count: (previous.count || 0) + 1,
    };
    await writeJsonBlob(ACCESS_BLOB, { accesses });
  } catch (err) {
    // Falha silenciosa — não queremos bloquear login por causa de log
    console.warn("logAccess falhou:", err);
  }
}

// ── Reações aos recados (👍 / 👎 por usuário) ────────────────
// Formato atual: { reactions: { "<annId>": { "<email>": { r: "up"|"down", n: "Nome" } } } }
// Compat: valores antigos podem ser a string "up"/"down" (sem nome) — a UI normaliza.
export async function loadReactions() {
  try {
    return await readJsonBlob(REACTIONS_BLOB);
  } catch {
    return { reactions: {} };
  }
}

export async function setReaction({ announcementId, email, name, reaction }) {
  if (!announcementId || !email) return;
  const key = email.toLowerCase();
  const current = await loadReactions();
  const reactions = current.reactions || {};
  const forAnn = { ...(reactions[announcementId] || {}) };
  if (reaction === "up" || reaction === "down") {
    forAnn[key] = { r: reaction, n: name || "" };
  } else {
    delete forAnn[key]; // reaction nula/qualquer outra coisa = remover
  }
  if (Object.keys(forAnn).length === 0) delete reactions[announcementId];
  else reactions[announcementId] = forAnn;
  await writeJsonBlob(REACTIONS_BLOB, { reactions });
}

// ── Controle de "lidos" para o sino de notificações ──────────
// Formato: { reads: { "<email>": { readIds: ["<annId>", ...] } } }
// Cada recado é marcado como lido individualmente quando o usuário clica nele.
export async function loadReads() {
  try {
    return await readJsonBlob(READS_BLOB);
  } catch {
    return { reads: {} };
  }
}

export async function markAnnouncementRead({ email, announcementId }) {
  if (!email || !announcementId) return;
  const key = email.toLowerCase();
  const current = await loadReads();
  const reads = current.reads || {};
  const entry = reads[key] || {};
  const readIds = Array.isArray(entry.readIds) ? entry.readIds : [];
  if (readIds.includes(announcementId)) return; // já estava lido
  reads[key] = { ...entry, readIds: [...readIds, announcementId] };
  await writeJsonBlob(READS_BLOB, { reads });
}

// ── Log de alterações (últimos 15 dias) ──────────────────────
export async function loadAuditLog() {
  try {
    const data = await readJsonBlob(AUDIT_BLOB);
    const cutoff = Date.now() - AUDIT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const events = (data.events || [])
      .filter(e => e.timestamp && new Date(e.timestamp).getTime() >= cutoff)
      .sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""));
    return { events };
  } catch {
    return { events: [] };
  }
}

export async function logAudit(entries) {
  // entries: [{ user, action, target, details, timestamp? }]
  if (!entries || entries.length === 0) return;
  try {
    let current;
    try { current = await readJsonBlob(AUDIT_BLOB); }
    catch { current = { events: [] }; }
    const now = new Date().toISOString();
    const cutoff = Date.now() - AUDIT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const kept = (current.events || []).filter(e =>
      e.timestamp && new Date(e.timestamp).getTime() >= cutoff
    );
    const enriched = entries.map(e => ({
      timestamp: e.timestamp || now,
      user: e.user || "",
      action: e.action || "",
      target: e.target || "",
      details: e.details || "",
    }));
    await writeJsonBlob(AUDIT_BLOB, { events: [...kept, ...enriched] });
  } catch (err) {
    console.warn("logAudit falhou:", err);
  }
}

function getDefaultUsers() {
  return {
    users: [
      { email: "raiane.flores@powertuning.com.br", role: "admin" },
    ],
  };
}

function getDefaultLinks() {
  return {
    public: [
      {
        id: "documentacao",
        label: "Documentação",
        items: [
          { id: "1", title: "PowerDocs", desc: "Bem-vindo ao portal de documentação", url: "#", color: "blue" },
          { id: "2", title: "Docs do PowerDocs", desc: "Como navegar, pesquisar e usar", url: "#", color: "blue" },
          { id: "3", title: "Docs de clientes", desc: "Horas, particularidades por cliente", url: "#", color: "purple" },
          { id: "4", title: "Universidade Power", desc: "Plano de carreira e aprendizado", url: "#", color: "purple" },
        ],
      },
      {
        id: "sistemas",
        label: "Sistemas internos",
        items: [
          { id: "5", title: "PowerON", desc: "Registro de folgas e disponibilidade", url: "#", color: "teal" },
          { id: "6", title: "PowerPlantão", desc: "Controle da escala de plantão", url: "#", color: "teal" },
          { id: "7", title: "PowerTickets", desc: "Demandas, horas e horas extras", url: "#", color: "amber" },
        ],
      },
      {
        id: "rh",
        label: "RH & equipe",
        items: [
          { id: "8", title: "RH", desc: "Contratação, procedimentos, políticas", url: "#", color: "coral" },
          { id: "9", title: "Geral da Power", desc: "Informações gerais da empresa", url: "#", color: "coral" },
        ],
      },
      {
        id: "dados",
        label: "Dados & relatórios",
        items: [
          { id: "10", title: "Relatórios Power", desc: "Central de relatórios diversos", url: "#", color: "teal" },
          { id: "11", title: "Grafana", desc: "Relatórios backup do PowerBI", url: "#", color: "teal" },
          { id: "12", title: "Comparar desempenho", desc: "Comparativo de desempenho de query", url: "#", color: "amber" },
        ],
      },
      {
        id: "dev",
        label: "Dev & arquivos",
        items: [
          { id: "13", title: "Git", desc: "Repositório de projetos e sistemas", url: "#", color: "gray" },
          { id: "14", title: "SharePoint", desc: "Central de arquivos compartilhados", url: "#", color: "gray" },
        ],
      },
    ],
    restricted: [
      {
        id: "lideranca",
        label: "Liderança",
        items: [
          { id: "15", title: "DashHoras", desc: "Dashboard de horas — Power Tuning", url: "#", color: "purple" },
        ],
      },
    ],
  };
}
